import {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import * as reduxSelectors from "components/store/application.reducers";
import {eventBus} from "components/pagelayout/EventBus";
import type {AccountingGridPanelProps} from "types/editor";
import AccountingGridNew from "./AccountingGridNew.component";
import {EditorModuleMenu} from "./EditorModuleMenu.component";
import "../../style/editor.css";

const TITLE_ID = "accounting-grid-module-title";
// the grid is as tall as its lines, from a floor of three, up to the window's height less this
const ROW_HEIGHT = 30;
const GRID_CHROME_HEIGHT = 120;
const MIN_GRID_HEIGHT = 200;
const WINDOW_MARGIN = 240;
// the grid reports when it is toggled and when a line change needs a warning, but nothing on the page uses these yet
const ignoreGridReport = () => undefined;

/**
 * The accounting module of the redesigned task details page: the accounting lines of the task, with how
 * many of them are shown in the title, and the module menu that detaches the editor modules into their own window.
 * It follows the width it is given, in the page or in the detached window.
 * It replaces taskdetails-remake/panels/editor/AccountingGridPanel.component on the redesigned page.
 */
export const AccountingGridPanel = (props: AccountingGridPanelProps) => {
    const {task, taskDetails, translate, renderInPortal = false} = props;
    const companyId: number | string = useSelector(reduxSelectors.getUsersCurrentContextId);
    const accountingGridRef = useRef<HTMLDivElement>(null);
    const [gridWidth, setGridWidth] = useState<number | undefined>(document.body.clientWidth);
    const [{rows, totalRows}, setRows] = useState({rows: 0, totalRows: 0});

    // in the detached window the grid lives in another window, whose size it has to follow
    const getOwnerWindow = () => accountingGridRef.current?.ownerDocument?.defaultView ?? window;

    const updateGridWidth = useCallback(() => setGridWidth(accountingGridRef.current?.clientWidth), []);

    useEffect(() => {
        updateGridWidth();
        eventBus.on("@@grid/refreshGridLayout", updateGridWidth);
        const ownerWindow = getOwnerWindow();
        ownerWindow?.addEventListener("resize", updateGridWidth);

        let resizeObserver: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined" && accountingGridRef.current) {
            resizeObserver = new ResizeObserver(() => updateGridWidth());
            resizeObserver.observe(accountingGridRef.current);
        }

        return () => {
            ownerWindow?.removeEventListener("resize", updateGridWidth);
            resizeObserver?.disconnect();
        };
    }, [updateGridWidth]);

    const onUpdateRows = useCallback((update: {rows: number, totalRows: number}) => setRows({rows: update.rows, totalRows: update.totalRows}), []);

    const accountingGridVisible = task?.hasAccountingGrid || task?.allowGridEdit || taskDetails?.allowGridEdit;
    if (!accountingGridVisible) {
        return null;
    }

    const panelHeight = totalRows < 3 ? MIN_GRID_HEIGHT : (totalRows * ROW_HEIGHT) + GRID_CHROME_HEIGHT;
    const ownerWindow = getOwnerWindow();
    const ownerDocument = accountingGridRef.current?.ownerDocument ?? document;
    const windowHeight = ownerWindow?.innerHeight || ownerDocument.documentElement.clientHeight || ownerDocument.body.clientHeight;
    const relativePanelHeight = panelHeight >= windowHeight - WINDOW_MARGIN ? windowHeight - WINDOW_MARGIN : panelHeight;

    return (
        // grid-layout-accounting-grid scopes the grid's table layout (taskdetails-remake/style/taskdetail.scss) over the global #accountingGrid rules
        <section className={`accounting-module grid-layout-accounting-grid${renderInPortal ? " accounting-module--side" : ""}`}
                 data-cy="taskDetails.accountingGridPanel"
                 aria-labelledby={TITLE_ID}>
            <header className="accounting-module__header">
                <h2 id={TITLE_ID} className="accounting-module__title">
                    {translate("accoutingGrid.Accounting")}
                    {rows !== 0 && totalRows !== 0 &&
                        <span className="accounting-module__count">
                            {`(${rows} ${translate("accoutingGrid.of")} ${totalRows})`}
                        </span>}
                </h2>
                {!props.inDetachedWindow &&
                    <EditorModuleMenu dataCy="taskDetails.accountingGridPanel" translate={translate}/>}
            </header>
            <div className="accounting-module__body">
                <div className="accounting-grid" ref={accountingGridRef} style={{height: relativePanelHeight}}>
                    <AccountingGridNew className="h-100"
                                       width={gridWidth}
                                       taskId={props.params.taskId}
                                       translate={translate}
                                       readOnly={props.isHistory || task.isHandled}
                                       language={props.userLanguage}
                                       lineChangeWarning={ignoreGridReport}
                                       companyId={companyId}
                                       onUpdateRows={onUpdateRows}
                                       toggle={ignoreGridReport}/>
                </div>
            </div>
        </section>
    );
};

export default AccountingGridPanel;
